import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Banner, Button, Card, Field, LoadingScreen, Row } from '@cowoo/shared/ui-web';
import { ApiProblem, formatTime } from '@cowoo/shared';
import { api } from '../../lib/api';
import { onAdminEvent } from '../../lib/sse';
import { useAdminStore } from '../../lib/store';
import { Dialog, Field as DialogField, fieldClass } from '../../ui';
import { toast } from '../../ui/toast';
import { TerminalConfig } from './TerminalConfig';
import { TerminalLog } from './TerminalLog';

/**
 * `/terminals` (10-web-admin § 15, protocol § 6).
 *
 * The remote open is the one action with a **mandatory** reason: it physically
 * opens a door and lands in the audit log (D-077). The UI enforces the reason so
 * staff are never surprised by a 422 after the fact. Its result comes back over
 * SSE — the server publishes every command ack on `terminal_ack` with the
 * `commandId` — so "Tür ist offen." is what the terminal said, not what the
 * request hoped.
 *
 * Adding, rebooting, updating and retiring a terminal are `terminals.manage`
 * (owner); staff see the cards, open, sync and identify, and the live device
 * log (`TerminalLog`, protocol § 6.1).
 */

/** D-077 — the open command expires after 15 s; a silent terminal is reported then. */
const OPEN_ANSWER_S = 15;

interface CommandAck {
  commandId: string;
  ok: boolean;
  code: string | null;
}

interface Enrolment {
  terminalId: string;
  code: string;
  expiresAt: string;
}

type Confirm = { kind: 'reboot' | 'update' | 'retire' | 'remove'; id: string; label: string } | null;

export function Terminals() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const can = useAdminStore((state) => state.can);
  const [reason, setReason] = useState('');
  const [problem, setProblem] = useState<ApiProblem | null>(null);
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newLocation, setNewLocation] = useState('');
  const [enrolment, setEnrolment] = useState<Enrolment | null>(null);
  const [confirm, setConfirm] = useState<Confirm>(null);
  const [typed, setTyped] = useState('');
  const [logOf, setLogOf] = useState<{ id: string; label: string } | null>(null);
  const waitingFor = useRef<string | null>(null);

  const terminals = useQuery({
    queryKey: ['admin', 'terminals'],
    queryFn: async () => {
      const { data, error } = await api.GET('/admin/terminals');
      if (error) throw error;
      // The items, as the door-events screen caches under the same key.
      return data.items;
    },
    refetchInterval: 30_000,
  });

  const locations = useQuery({
    queryKey: ['admin', 'locations'],
    enabled: can('terminals.manage'),
    queryFn: async () => {
      const { data, error } = await api.GET('/admin/locations', {});
      if (error) throw error;
      return data.items;
    },
  });

  // The open's answer. Anything else the terminal acks is not this screen's.
  useEffect(() => {
    const stop = onAdminEvent('terminal_ack', (data) => {
      const ack = data as CommandAck;
      if (!waitingFor.current || ack.commandId !== waitingFor.current) return;
      waitingFor.current = null;
      if (ack.ok) toast(t('admin.terminals.doorOpened'), { tone: 'success' });
      else toast(t(`admin.terminals.result.${ack.code ?? 'failed'}`), { tone: 'danger' });
    });
    return stop;
  }, [t]);

  const command = useMutation({
    mutationFn: async (input: { id: string; type: 'open' | 'sync' | 'identify' | 'reboot' | 'update' }) => {
      const { data, error } = await api.POST('/admin/terminals/{id}/commands', {
        params: { path: { id: input.id } },
        body: { type: input.type, ...(input.type === 'open' ? { reason } : {}) },
      });
      if (error) throw error;
      return { ...data, type: input.type };
    },
    onSuccess: (result) => {
      setReason('');
      setProblem(null);
      setConfirm(null);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'terminals'] });
      if (result.type === 'open') {
        waitingFor.current = result.commandId;
        window.setTimeout(() => {
          if (waitingFor.current !== result.commandId) return;
          waitingFor.current = null;
          toast(t('admin.terminals.result.no_answer'), { tone: 'danger' });
        }, OPEN_ANSWER_S * 1000);
      } else {
        toast(t('admin.terminals.commandSent'));
      }
    },
    onError: (error) => setProblem(error instanceof ApiProblem ? error : null),
  });

  const create = useMutation({
    mutationFn: async () => {
      const { data, error } = await api.POST('/admin/terminals', {
        body: { label: newLabel.trim(), locationId: newLocation },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      setProblem(null);
      setAdding(false);
      setEnrolment(data);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'terminals'] });
    },
    onError: (error) => setProblem(error instanceof ApiProblem ? error : null),
  });

  const retire = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api.POST('/admin/terminals/{id}/retire', {
        params: { path: { id } },
        body: { reason: reason.trim() },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setProblem(null);
      setConfirm(null);
      setReason('');
      toast(t('admin.terminals.retired'));
      void queryClient.invalidateQueries({ queryKey: ['admin', 'terminals'] });
    },
    onError: (error) => setProblem(error instanceof ApiProblem ? error : null),
  });

  /** § 15 — only after retiring: the row goes, its door events stay without a label. */
  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api.DELETE('/admin/terminals/{id}', {
        params: { path: { id } },
        body: { reason: reason.trim(), confirmLabel: typed.trim() },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setProblem(null);
      setConfirm(null);
      setReason('');
      toast(t('admin.terminals.removed'));
      void queryClient.invalidateQueries({ queryKey: ['admin', 'terminals'] });
    },
    onError: (error) => setProblem(error instanceof ApiProblem ? error : null),
  });

  if (terminals.isLoading) return <LoadingScreen label={t('common.loading')} />;

  const manage = can('terminals.manage');
  // A retired terminal is no longer in service: it gets its own section below,
  // without commands or settings, instead of passing for an offline one.
  const active = (terminals.data ?? []).filter((terminal) => terminal.status !== 'retired');
  const retired = (terminals.data ?? []).filter((terminal) => terminal.status === 'retired');
  const openLocations = (locations.data ?? []).filter((location) => location.status === 'open');
  const reasonOk = reason.trim().length >= 5;

  return (
    <main className="max-w-content mx-auto px-5 py-10">
      <div className="mb-6 flex items-center justify-between gap-3">
        <h1 className="text-webH3 text-ink">{t('admin.terminals.title')}</h1>
        <div className="flex items-center gap-2">
          <Link
            to="/terminals/ereignisse"
            className="text-teal-dark font-body text-sm font-semibold underline"
          >
            {t('admin.terminals.events')}
          </Link>
          {manage ? (
            <Button
              size="sm"
              onClick={() => {
                setNewLabel('');
                setNewLocation(openLocations[0]?.id ?? locations.data?.[0]?.id ?? '');
                setAdding(true);
              }}
            >
              {t('admin.terminals.add')}
            </Button>
          ) : null}
        </div>
      </div>

      {problem && !adding && !confirm ? <Banner tone="danger">{t(problem.i18nKey)}</Banner> : null}

      <div className="mt-4 grid gap-4 md:grid-cols-2">
        {active.map((terminal) => (
          <Card key={terminal.id}>
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-title text-ink">{terminal.label}</h2>
              <span
                className={`font-body rounded-pill px-3 py-1 text-xs font-semibold ${
                  terminal.online ? 'bg-success-bg text-success-text' : 'bg-danger-bg text-danger-text'
                }`}
              >
                {terminal.online ? t('admin.terminals.online') : offlineLabel(terminal.lastSeenAt, t)}
              </span>
            </div>

            <Row
              label={t('admin.terminals.lastSeen')}
              value={terminal.lastSeenAt ? formatTime(terminal.lastSeenAt, true) : '—'}
            />
            <Row label={t('admin.terminals.version')} value={terminal.swVersion ?? '—'} />
            <Row label={t('admin.terminals.certUntil')} value={terminal.certNotAfter?.slice(0, 10) ?? '—'} />

            {can('door.open') ? (
              <div className="mt-4 flex flex-col gap-3">
                <Field
                  label={t('admin.terminals.reason')}
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  hint={t('admin.terminals.reasonHint')}
                />
                <p className="text-adminSmall text-muted">
                  {t('admin.terminals.openNote', {
                    seconds: Math.round(Number(terminal.config?.pulse_ms ?? 5000) / 1000),
                  })}
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    disabled={!reasonOk}
                    loading={command.isPending}
                    onClick={() => command.mutate({ id: terminal.id, type: 'open' })}
                  >
                    {t('admin.terminals.open')}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => command.mutate({ id: terminal.id, type: 'sync' })}
                  >
                    {t('admin.terminals.sync')}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => command.mutate({ id: terminal.id, type: 'identify' })}
                  >
                    {t('admin.terminals.identify')}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => setLogOf({ id: terminal.id, label: terminal.label })}
                    data-testid="terminal-log-open"
                  >
                    {t('admin.terminals.log.open')}
                  </Button>
                </div>
                {manage ? (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => setConfirm({ kind: 'reboot', id: terminal.id, label: terminal.label })}
                    >
                      {t('admin.terminals.reboot')}
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => setConfirm({ kind: 'update', id: terminal.id, label: terminal.label })}
                    >
                      {t('admin.terminals.update')}
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => {
                        setTyped('');
                        setConfirm({ kind: 'retire', id: terminal.id, label: terminal.label });
                      }}
                    >
                      {t('admin.terminals.retire')}
                    </Button>
                  </div>
                ) : null}
              </div>
            ) : null}

            {/* § 12 — the settings the protocol lets an operator change. Owner
                only, because `PATCH …/config` is, and a form that 403s on save
                is worse than no form. */}
            {can('settings.edit') ? (
              <TerminalConfig
                id={terminal.id}
                config={(terminal.config ?? {}) as Record<string, unknown>}
                configRev={terminal.configRev ?? 0}
              />
            ) : null}
          </Card>
        ))}
      </div>

      {retired.length > 0 ? (
        <section className="mt-10" data-testid="terminals-retired">
          <h2 className="text-title text-ink mb-1">{t('admin.terminals.retiredTitle')}</h2>
          <p className="text-adminSmall text-muted mb-4">{t('admin.terminals.retiredHint')}</p>
          <div className="grid gap-4 md:grid-cols-2">
            {retired.map((terminal) => (
              <Card key={terminal.id}>
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="text-title text-muted">{terminal.label}</h3>
                  <span className="font-body rounded-pill bg-fog text-muted px-3 py-1 text-xs font-semibold">
                    {t('admin.terminals.retiredChip')}
                  </span>
                </div>
                <Row
                  label={t('admin.terminals.lastSeen')}
                  value={terminal.lastSeenAt ? formatTime(terminal.lastSeenAt, true) : '—'}
                />
                <Row label={t('admin.terminals.version')} value={terminal.swVersion ?? '—'} />
                {manage ? (
                  <div className="mt-4">
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => {
                        setTyped('');
                        setReason('');
                        setConfirm({ kind: 'remove', id: terminal.id, label: terminal.label });
                      }}
                    >
                      {t('admin.terminals.remove')}
                    </Button>
                  </div>
                ) : null}
              </Card>
            ))}
          </div>
        </section>
      ) : null}

      {logOf ? <TerminalLog terminal={logOf} onClose={() => setLogOf(null)} /> : null}

      {adding ? (
        <Dialog
          title={t('admin.terminals.add')}
          confirmLabel={t('admin.terminals.addSubmit')}
          onConfirm={() => create.mutate()}
          onClose={() => {
            setAdding(false);
            setProblem(null);
          }}
          busy={create.isPending}
          disabled={newLabel.trim().length === 0 || newLocation === ''}
        >
          {problem ? <Banner tone="danger">{t(problem.i18nKey)}</Banner> : null}
          <DialogField label={t('admin.terminals.addLabel')}>
            <input
              value={newLabel}
              onChange={(event) => setNewLabel(event.target.value)}
              placeholder={t('admin.terminals.addLabelPlaceholder')}
              className={fieldClass}
            />
          </DialogField>
          <DialogField label={t('admin.terminals.addLocation')}>
            <select
              value={newLocation}
              onChange={(event) => setNewLocation(event.target.value)}
              className={fieldClass}
            >
              {(locations.data ?? []).map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </DialogField>
        </Dialog>
      ) : null}

      {enrolment ? (
        <EnrolCodeDialog
          enrolment={enrolment}
          onClose={() => setEnrolment(null)}
          onRenew={() => {
            // A fresh code for the same terminal: the server treats a second
            // POST as a new terminal, so the renewed one is a new enrolment of
            // the same label. The stale card is cleaned up by the list refetch.
            setEnrolment(null);
            setNewLabel(terminals.data?.find((item) => item.id === enrolment.terminalId)?.label ?? '');
            setAdding(true);
          }}
        />
      ) : null}

      {/* Retiring and removing are both confirmed by typing the label and giving a reason. */}
      {confirm?.kind === 'retire' || confirm?.kind === 'remove' ? (
        <Dialog
          title={
            confirm.kind === 'remove' ? t('admin.terminals.removeTitle') : t('admin.terminals.retireTitle')
          }
          intro={
            confirm.kind === 'remove'
              ? t('admin.terminals.removeBody', { label: confirm.label })
              : t('admin.terminals.retireBody', { label: confirm.label })
          }
          tone="danger"
          confirmLabel={confirm.kind === 'remove' ? t('admin.terminals.remove') : t('admin.terminals.retire')}
          onConfirm={() =>
            confirm.kind === 'remove' ? remove.mutate(confirm.id) : retire.mutate(confirm.id)
          }
          onClose={() => {
            setConfirm(null);
            setProblem(null);
          }}
          busy={confirm.kind === 'remove' ? remove.isPending : retire.isPending}
          disabled={typed.trim() !== confirm.label || !reasonOk}
        >
          {problem ? <Banner tone="danger">{t(problem.i18nKey)}</Banner> : null}
          <DialogField label={t('admin.terminals.retireConfirm')}>
            <input value={typed} onChange={(event) => setTyped(event.target.value)} className={fieldClass} />
          </DialogField>
          <DialogField label={t('admin.terminals.reason')} hint={t('admin.reasonHint')}>
            <input
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder={t('admin.reasonPlaceholder')}
              className={fieldClass}
            />
          </DialogField>
        </Dialog>
      ) : null}

      {confirm && (confirm.kind === 'reboot' || confirm.kind === 'update') ? (
        <Dialog
          title={t(`admin.terminals.${confirm.kind}`)}
          intro={
            confirm.kind === 'reboot'
              ? t('admin.terminals.rebootBody')
              : t('admin.terminals.updateBody', {
                  version: String(
                    terminals.data?.find((item) => item.id === confirm.id)?.config?.target_version ?? '—',
                  ),
                })
          }
          confirmLabel={t(`admin.terminals.${confirm.kind}`)}
          onConfirm={() =>
            command.mutate({ id: confirm.id, type: confirm.kind === 'update' ? 'update' : 'reboot' })
          }
          onClose={() => {
            setConfirm(null);
            setProblem(null);
          }}
          busy={command.isPending}
        >
          {problem ? <Banner tone="danger">{t(problem.i18nKey)}</Banner> : null}
        </Dialog>
      ) : null}
    </main>
  );
}

type T = ReturnType<typeof useTranslation>['t'];

/** "offline seit 12 Min." when the last contact is known, plain "offline" otherwise. */
function offlineLabel(lastSeenAt: string | null, t: T): string {
  if (!lastSeenAt) return t('admin.terminals.offline');
  const minutes = Math.max(0, Math.round((Date.now() - new Date(lastSeenAt).getTime()) / 60_000));
  return t('admin.terminals.offlineSince', { minutes });
}

/**
 * § 15 — the enrol code, shown once. Eight Crockford characters grouped 4-4 in
 * monospace, the countdown to its expiry, "Kopieren". Closing discards it.
 */
function EnrolCodeDialog({
  enrolment,
  onClose,
  onRenew,
}: {
  enrolment: Enrolment;
  onClose: () => void;
  onRenew: () => void;
}) {
  const { t } = useTranslation();
  const [now, setNow] = useState(() => Date.now());
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const tick = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(tick);
  }, []);

  const left = Math.max(0, Math.floor((new Date(enrolment.expiresAt).getTime() - now) / 1000));
  const mm = String(Math.floor(left / 60)).padStart(2, '0');
  const ss = String(left % 60).padStart(2, '0');
  const grouped = enrolment.code.replace(/(.{4})(?=.)/g, '$1 ');

  return (
    <Dialog
      title={t('admin.terminals.enrolTitle')}
      intro={t('admin.terminals.enrolHint')}
      confirmLabel={t('admin.terminals.newCode')}
      onConfirm={onRenew}
      onClose={onClose}
    >
      <p
        className="text-ink text-center font-mono text-[40px] font-bold tracking-[0.12em]"
        aria-live="polite"
      >
        {grouped}
      </p>
      <p className="text-adminSmall text-muted text-center">
        {left > 0
          ? t('admin.terminals.enrolValidity', { time: `${mm}:${ss}` })
          : t('admin.terminals.enrolExpired')}
      </p>
      <div className="flex justify-center">
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            void navigator.clipboard?.writeText(enrolment.code).then(() => setCopied(true));
          }}
        >
          {copied ? t('admin.terminals.copied') : t('admin.terminals.copy')}
        </Button>
      </div>
      <p className="text-adminSmall text-muted">{t('admin.terminals.enrolOnce')}</p>
    </Dialog>
  );
}
